/**
 * DevTools - Development tools panel
 *
 * Provides debugging information overlay with FPS counter,
 * player position, state information, and more.
 */

export class DevTools {
  constructor() {
    this.enabled = false;
    this.panel = null;
    this.fps = 0;
    this.frameCount = 0;
    this.lastFpsUpdate = performance.now();
    this.dataProviders = new Map();

    this.createPanel();
    this.setupKeyboardToggle();
  }

  /**
   * Create the dev tools panel UI
   */
  createPanel() {
    const panel = document.createElement('div');
    panel.id = 'dev-tools';
    panel.className = 'dev-tools hidden';
    panel.innerHTML = `
      <div class="dev-tools-header">
        <span class="dev-tools-title">🛠️ Dev Tools</span>
        <span class="dev-tools-hint">Press F3 to toggle</span>
      </div>
      <div class="dev-tools-content">
        <div class="dev-section">
          <div class="dev-label">FPS:</div>
          <div class="dev-value" id="dev-fps">0</div>
        </div>
        <div class="dev-section">
          <div class="dev-label">Player Position:</div>
          <div class="dev-value" id="dev-player-pos">x: 0, y: 0</div>
        </div>
        <div class="dev-section">
          <div class="dev-label">Player Velocity:</div>
          <div class="dev-value" id="dev-player-vel">vx: 0, vy: 0</div>
        </div>
        <div class="dev-section">
          <div class="dev-label">Camera:</div>
          <div class="dev-value" id="dev-camera">x: 0, y: 0</div>
        </div>
        <div class="dev-section">
          <div class="dev-label">State:</div>
          <div class="dev-value" id="dev-state">-</div>
        </div>
        <div id="dev-custom-sections"></div>
      </div>
    `;

    document.body.appendChild(panel);
    this.panel = panel;
  }

  /**
   * Setup F3 keyboard toggle
   */
  setupKeyboardToggle() {
    window.addEventListener('keydown', (e) => {
      if (e.key === 'F3') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  /**
   * Toggle dev tools visibility
   */
  toggle() {
    this.enabled = !this.enabled;
    if (this.panel) {
      this.panel.classList.toggle('hidden', !this.enabled);
    }
  }

  /**
   * Show dev tools
   */
  show() {
    this.enabled = true;
    if (this.panel) {
      this.panel.classList.remove('hidden');
    }
  }

  /**
   * Hide dev tools
   */
  hide() {
    this.enabled = false;
    if (this.panel) {
      this.panel.classList.add('hidden');
    }
  }

  /**
   * Register a data provider for custom sections
   */
  registerDataProvider(name, provider) {
    this.dataProviders.set(name, provider);
    this.updateCustomSections();
  }

  /**
   * Unregister a data provider
   */
  unregisterDataProvider(name) {
    this.dataProviders.delete(name);
    this.updateCustomSections();
  }

  /**
   * Update custom sections in the panel
   */
  updateCustomSections() {
    const container = document.getElementById('dev-custom-sections');
    if (!container) return;

    // Clear existing custom sections
    container.innerHTML = '';

    // Add custom sections
    for (const [name, provider] of this.dataProviders) {
      const section = document.createElement('div');
      section.className = 'dev-section';
      section.innerHTML = `
        <div class="dev-label">${name}:</div>
        <div class="dev-value" id="dev-custom-${name.toLowerCase().replace(/\s+/g, '-')}">-</div>
      `;
      container.appendChild(section);
    }
  }

  /**
   * Update FPS counter
   */
  updateFPS(deltaTime) {
    this.frameCount++;
    const now = performance.now();
    const elapsed = now - this.lastFpsUpdate;

    // Update FPS every 500ms
    if (elapsed >= 500) {
      this.fps = Math.round((this.frameCount * 1000) / elapsed);
      this.frameCount = 0;
      this.lastFpsUpdate = now;

      const fpsEl = document.getElementById('dev-fps');
      if (fpsEl) {
        fpsEl.textContent = this.fps;

        // Color code FPS
        if (this.fps >= 55) {
          fpsEl.style.color = '#4ade80'; // green
        } else if (this.fps >= 30) {
          fpsEl.style.color = '#fbbf24'; // yellow
        } else {
          fpsEl.style.color = '#ef4444'; // red
        }
      }
    }
  }

  /**
   * Update player position
   */
  updatePlayerPosition(x, y) {
    const el = document.getElementById('dev-player-pos');
    if (el) {
      el.textContent = `x: ${Math.round(x)}, y: ${Math.round(y)}`;
    }
  }

  /**
   * Update player velocity
   */
  updatePlayerVelocity(vx, vy) {
    const el = document.getElementById('dev-player-vel');
    if (el) {
      el.textContent = `vx: ${Math.round(vx)}, vy: ${Math.round(vy)}`;
    }
  }

  /**
   * Update camera position
   */
  updateCamera(x, y) {
    const el = document.getElementById('dev-camera');
    if (el) {
      el.textContent = `x: ${Math.round(x)}, y: ${Math.round(y)}`;
    }
  }

  /**
   * Update state information
   */
  updateState(state) {
    const el = document.getElementById('dev-state');
    if (el) {
      el.textContent = state;
    }
  }

  /**
   * Update a custom data provider value
   */
  updateCustomData(name) {
    const provider = this.dataProviders.get(name);
    if (!provider) return;

    const id = `dev-custom-${name.toLowerCase().replace(/\s+/g, '-')}`;
    const el = document.getElementById(id);
    if (el) {
      const value = provider();
      el.textContent = typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
  }

  /**
   * Update all dev tools data (call this in your game loop)
   */
  update(data = {}) {
    if (!this.enabled) return;

    if (data.deltaTime !== undefined) {
      this.updateFPS(data.deltaTime);
    }

    if (data.player) {
      if (data.player.x !== undefined && data.player.y !== undefined) {
        this.updatePlayerPosition(data.player.x, data.player.y);
      }
      if (data.player.vx !== undefined && data.player.vy !== undefined) {
        this.updatePlayerVelocity(data.player.vx, data.player.vy);
      }
    }

    if (data.camera) {
      if (data.camera.x !== undefined && data.camera.y !== undefined) {
        this.updateCamera(data.camera.x, data.camera.y);
      }
    }

    if (data.state !== undefined) {
      this.updateState(data.state);
    }

    // Update custom data providers
    for (const name of this.dataProviders.keys()) {
      this.updateCustomData(name);
    }
  }

  /**
   * Get current FPS
   */
  getFPS() {
    return this.fps;
  }

  /**
   * Check if dev tools are enabled
   */
  isEnabled() {
    return this.enabled;
  }
}
